import { reserveNextLocalWithdrawalNumber } from "../storage/offlineDatabase";
import { getLastKnownAuthUser } from "./authService";

const confirmedKey = (userId: number) => `materialentnahme:last-confirmed-withdrawal-number:${userId}`;
const SERVER_NUMBER_TIMEOUT_MS = 1200;

export function rememberConfirmedWithdrawalNumber(userId: number, number: number | null | undefined): void {
  if (!number || number < 1) return;
  try {
    const current = Number(window.localStorage.getItem(confirmedKey(userId)) ?? "0");
    if (number > current) window.localStorage.setItem(confirmedKey(userId), String(number));
  } catch { /* optional cache */ }
}

async function fetchServerLastConfirmedNumber(userId: number, timeoutMs = SERVER_NUMBER_TIMEOUT_MS): Promise<number | null> {
  if (typeof navigator === "undefined" || !navigator.onLine) return null;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch("/api/withdrawals/numbering", {
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const payload = await response.json() as { last_confirmed_number?: number };
    const confirmed = Number(payload.last_confirmed_number ?? 0);
    if (confirmed > 0) rememberConfirmedWithdrawalNumber(userId, confirmed);
    return Number.isFinite(confirmed) ? confirmed : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Wärmt die serverseitig bestätigte persönliche Entnahmenummer direkt nach
 * erfolgreicher Anmeldung vor. Der Aufruf ist best effort und blockiert die UI nicht.
 */
export async function warmWithdrawalNumbering(userId?: number): Promise<void> {
  const owner = userId ?? getLastKnownAuthUser()?.id;
  if (!owner) return;
  await fetchServerLastConfirmedNumber(owner);
}

export async function nextLocalWithdrawalNumber(userId?: number): Promise<number> {
  const owner = userId ?? getLastKnownAuthUser()?.id;
  if (!owner) return 1;

  // Wenn der Server erreichbar ist, wird die aktuelle persönliche Nummer bewusst
  // VOR der lokalen Vergabe abgefragt. So führt ein gelöschter Browsercache nicht
  // wieder zu einer sichtbaren lokalen #1, obwohl serverseitig bereits Entnahmen
  // vorhanden sind. Bei Offlinebetrieb bleibt die Vergabe vollständig local-first.
  const serverConfirmed = await fetchServerLastConfirmedNumber(owner);

  let confirmed = serverConfirmed ?? 0;
  try {
    confirmed = Math.max(confirmed, Number(window.localStorage.getItem(confirmedKey(owner)) ?? "0"));
  } catch { /* optional */ }

  return reserveNextLocalWithdrawalNumber(owner, confirmed);
}

export function formatUserWithdrawalNumber(number: number | null | undefined, localNumber?: number | null): string {
  const visible = number ?? localNumber ?? null;
  return visible ? `#${visible}` : "Noch ohne Nummer";
}
